// The one full-screen player chrome every web player uses (spec 2026-10-24-fullscreen-player-controls).
// Figma: Shared / Player / Controls 4351:2706 (Platform=Web: portrait 4351:2346… landscape 4351:2526…), Round button
// 4350:1888, Seek bar 4350:1902. Embeds: web portrait 4351:2484, landscape 4351:2664.
import { useCallback, useEffect, useRef, type CSSProperties, type ReactNode } from "react";
import PlayerSeekBar from "./PlayerSeekBar";
import type { ControlButton, PlayerControlsSpec } from "./playerControlsSpec";
import { PlayerControlsContext, type PlayerControlsVisibility } from "./usePlayerControlsVisibility";

/** Shared / Player / Round button: 48 target · 32 disc white 16 % (large: 72 · 56 black 40 %; destructive: red disc). */
export function PlayerRoundButton({ button }: { button: ControlButton }) {
  const { variant = "default" } = button;
  const large = variant === "large";
  const disc =
    variant === "destructive" ? "bg-[var(--destructive)]" : large ? "bg-black/40" : "bg-white/16";
  return (
    <button
      type="button"
      data-testid={button.testId}
      aria-label={button.label}
      title={button.label}
      disabled={button.disabled}
      onClick={button.onClick}
      className={`group flex shrink-0 items-center justify-center rounded-full text-white disabled:opacity-40 focus-visible:outline-2 focus-visible:outline-white ${large ? "size-[72px]" : "size-12"}`}
    >
      <span
        className={`flex items-center justify-center rounded-full ${disc} ${
          large ? "size-14 [&_svg]:size-8" : "size-8 [&_svg]:size-[18px]"
        }`}
      >
        {button.icon}
      </span>
    </button>
  );
}

function TitleColumn({ spec }: { spec: PlayerControlsSpec }) {
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
      <div className="flex min-w-0 items-center gap-1.5">
        <span className="truncate text-base font-bold leading-tight text-white">{spec.title}</span>
        {spec.badge}
      </div>
      {spec.meta && <span className="truncate text-xs leading-[1.3] text-white/75">{spec.meta}</span>}
    </div>
  );
}

const buttons = (list: ControlButton[]) => list.map((b) => <PlayerRoundButton key={b.testId} button={b} />);

interface Props {
  spec: PlayerControlsSpec;
  visibility: PlayerControlsVisibility;
  /**
   * false = not in full screen: the player renders in place (every wrapper is `display: contents`, no chrome, no
   * handlers). The tree is the same in both forms, so entering / leaving full screen never remounts the player.
   */
  on?: boolean;
  /** The player (video / audio art / embed iframe). Overlay mode: fills the root under the chrome. Embed: the 16:9 area. */
  children?: ReactNode;
}

/** Hidden chrome leaves the tab order and the accessibility tree too, not only the eye. */
const hiddenProps = (visible: boolean) => (visible ? {} : { inert: true, "aria-hidden": true as const });

type FsDoc = Document & { webkitFullscreenElement?: Element | null };


/**
 * Full-screen player controls. Fills its parent (the element that went full screen) with black, draws the player,
 * and the controls: over the player (top bar + scrim, middle cluster, seek bar + scrim) or, for embeds, in bands
 * outside the 16:9 player. Fades in 200 ms; when hidden nothing of ours is over the video and the cursor hides.
 * Spread nothing else on it: the root carries the visibility handlers.
 */
export default function FullScreenPlayerControls({ spec, visibility, on = true, children }: Props) {
  const { visible: shown, bind } = visibility;
  const visible = shown || !on;
  const fade = `transition-opacity duration-200 ${visible ? "opacity-100" : "opacity-0 pointer-events-none"}`;
  const start = spec.top.filter((b) => b.start);
  const end = spec.top.filter((b) => !b.start);
  const hasTop = spec.title !== "" || spec.top.length > 0 || spec.belowTop != null;
  const hidden = hiddenProps(visible);

  // requestFullscreen() leaves focus where it was: on <body> (the enter button unmounted) or on a button outside the
  // full-screen element (the wall card's). Move it to the root, so Tab goes to the controls next and keys land here.
  const rootRef = useRef<HTMLDivElement | null>(null);
  const bindRef = bind.ref;
  const setRoot = useCallback((el: HTMLDivElement | null) => {
    rootRef.current = el;
    bindRef?.(el);
  }, [bindRef]);
  useEffect(() => {
    const root = rootRef.current;
    if (!on || !root) return;
    const doc = root.ownerDocument as FsDoc;
    const fsEl = doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
    if (fsEl?.contains(root) && !root.contains(doc.activeElement)) root.focus({ preventScroll: true });
  }, [on]);

  return (
    <PlayerControlsContext.Provider value={on ? visibility : null}>
      <div
        data-testid={on ? "player-controls" : undefined}
        data-visible={on ? (visible ? "true" : "false") : undefined}
        className={on ? "relative size-full overflow-hidden bg-black text-white outline-none [container-type:size]" : "contents"}
        style={visible ? undefined : { cursor: "none" }}
        tabIndex={on ? -1 : undefined}
        {...(on ? bind : {})}
        ref={setRoot}
      >
        {spec.embed === "bandUnder" ? (
          <BandUnder spec={spec} start={start} end={end} fade={fade} on={on} hidden={hidden}>{children}</BandUnder>
        ) : spec.embed === "sideBands" ? (
          <SideBands spec={spec} start={start} end={end} fade={fade} on={on} hidden={hidden}>{children}</SideBands>
        ) : (
          <>
            <div className={on ? "absolute inset-0" : "contents"}>{children}</div>
            {/* Never takes taps itself: the gaps fall through to the player (and the root's tap toggle). */}
            {on && (
              <div data-testid="player-controls-chrome" data-player-chrome className={`pointer-events-none absolute inset-0 ${fade}`} {...hidden}>
                {hasTop && (
                  <div className={`absolute inset-x-0 top-0 isolate ${visible ? "pointer-events-auto" : ""}`}>
                    <div aria-hidden="true" className="absolute inset-x-0 top-0 -z-10 h-[calc(100%+40px)] bg-linear-to-b from-black/60 to-transparent" />
                    <div className="px-2 pt-2 pb-1 landscape:px-4">
                      <div data-testid="player-controls-top" className="flex items-center gap-1">
                        {buttons(start)}
                        <TitleColumn spec={spec} />
                        {buttons(end)}
                      </div>
                      {spec.belowTop && <div className="pl-[52px] empty:hidden">{spec.belowTop}</div>}
                    </div>
                  </div>
                )}
                {spec.middle.length > 0 && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className={`flex items-center gap-8 ${visible ? "pointer-events-auto" : ""}`}>{buttons(spec.middle)}</div>
                  </div>
                )}
                {spec.seek ? (
                  <div className={`absolute inset-x-0 bottom-0 isolate px-4 pt-2 pb-3 landscape:px-6 ${visible ? "pointer-events-auto" : ""}`}>
                    <div aria-hidden="true" className="absolute inset-x-0 bottom-0 -z-10 h-[calc(100%+40px)] bg-linear-to-t from-black/60 to-transparent" />
                    <PlayerSeekBar
                      {...spec.seek}
                      onDragChange={(dragging) => visibility.hold("seeking", dragging)}
                    />
                    {spec.bottomExtra && <div className="mt-2 flex justify-center">{spec.bottomExtra}</div>}
                  </div>
                ) : (
                  spec.bottomExtra && (
                    <div className="absolute inset-x-0 bottom-4 flex justify-center">
                      <div className={visible ? "pointer-events-auto" : ""}>{spec.bottomExtra}</div>
                    </div>
                  )
                )}
              </div>
            )}
          </>
        )}
      </div>
    </PlayerControlsContext.Provider>
  );
}

interface BandProps {
  spec: PlayerControlsSpec;
  start: ControlButton[];
  end: ControlButton[];
  fade: string;
  on: boolean;
  hidden: ReturnType<typeof hiddenProps>;
  children?: ReactNode;
}

/**
 * Embed, band under the 16:9 player (web portrait 4351:2484 / landscape 4351:2664). Portrait: row 1 = exit · title ·
 * stop, row 2 = previous · next centred (gap 32). Landscape: one 72 px row = previous · next | title | exit · stop.
 * The player keeps 16:9 inside what the band leaves.
 */
function BandUnder({ spec, start, end, fade, on, hidden, children }: BandProps) {
  const column: CSSProperties = { width: "min(100cqw, calc((100cqh - var(--band)) * 16 / 9))" };
  return (
    // An extra row present: the band grows by it, so the player keeps 16:9 and the band is never cut off.
    <div
      className={
        on
          ? "flex size-full items-center justify-center [--band:112px] landscape:[--band:72px] portrait:has-[[data-band-row]:not(:empty)]:[--band:152px] landscape:has-[[data-band-row]:not(:empty)]:[--band:112px]"
          : "contents"
      }
    >
      <div className={on ? "flex flex-col" : "contents"} style={on ? column : undefined}>
        <div data-testid={on ? "player-controls-player" : undefined} className={on ? "relative aspect-video w-full overflow-hidden" : "contents"}>{children}</div>
        {on && (
        <div data-testid="player-controls-band" className={`flex min-h-(--band) flex-col justify-center ${fade}`} {...hidden}>
          <div
            data-testid="player-controls-top"
            className="flex flex-wrap items-center gap-x-1 px-2 py-1 landscape:flex-nowrap landscape:gap-4 landscape:px-0"
          >
            <div className="order-1 flex items-center landscape:order-3">{buttons(start)}</div>
            <div className="order-2 flex min-w-0 flex-1">
              <TitleColumn spec={spec} />
            </div>
            <div className="order-3 flex items-center gap-1 landscape:order-4">{buttons(end)}</div>
            {spec.middle.length > 0 && (
              <div className="order-4 flex basis-full items-center justify-center gap-8 landscape:order-1 landscape:basis-auto landscape:gap-2">
                {buttons(spec.middle)}
              </div>
            )}
          </div>
          {/* Extra rows (queue line, Tap to listen): each its own full-width row under the top row, never inside it
              (a full-width item in the one-line landscape row squeezed the title to 0 px). `empty:hidden`: content
              that renders nothing (a one-item queue's CastQueueLine) takes no room. */}
          {spec.belowTop && <div data-band-row="" className="pr-2 pl-[52px] empty:hidden landscape:px-0">{spec.belowTop}</div>}
          {spec.bottomExtra && <div data-band-row="" className="flex justify-center px-2 pb-1 empty:hidden">{spec.bottomExtra}</div>}
        </div>
        )}
      </div>
    </div>
  );
}

/**
 * Embed, bands left and right of the 16:9 player (Android landscape 4351:2295): left = exit at the top, previous /
 * next centred; right = the other top buttons. No title (the embed shows its own), no queue line.
 */
function SideBands({ spec, start, end, fade, on, hidden, children }: BandProps) {
  const player: CSSProperties = { height: "min(100cqh, calc((100cqw - 160px) * 9 / 16))" };
  return (
    <div className={on ? "flex size-full items-center justify-center" : "contents"}>
      {on && (
        <div data-testid="player-controls-band" className={`flex h-full w-20 shrink-0 flex-col items-center justify-between py-7 ${fade}`} {...hidden}>
          <div data-testid="player-controls-top" className="flex flex-col items-center gap-2">{buttons(start)}</div>
          <div className="flex flex-col items-center gap-2">{buttons(spec.middle)}</div>
          <div className="size-12" aria-hidden="true" />
        </div>
      )}
      <div data-testid={on ? "player-controls-player" : undefined} className={on ? "relative aspect-video overflow-hidden" : "contents"} style={on ? player : undefined}>{children}</div>
      {on && (
        <div data-testid="player-controls-band-end" className={`flex h-full w-20 shrink-0 flex-col items-center gap-2 py-7 ${fade}`} {...hidden}>
          {buttons(end)}
        </div>
      )}
    </div>
  );
}
